import { string } from 'prop-types';
import * as React from 'react';

export interface IQuantitySelect {
    quantity: number;
    canAdd: boolean;
    canRemove: boolean;
    onSelectQuantity: (newQuantity: number) => void;
    componentClassName: string;
    componentFocusedClassName?: string;
    arialabel?: string;
    maxQuantity?: number;
    increaseLabel?: string;
    decreaseLabel?: string;
};

interface IDataLoaderState {
    loadingText: string;
    showStopAnimationButton: boolean;
    showAnimation: boolean;
    timeout: any;
    isFocused: boolean;
};

interface IQuantitySelectState {    
    isFocused: boolean;
};

class QuantitySelect extends React.PureComponent<IQuantitySelect, IQuantitySelectState> {

    static SmallStep: number = 1;
    static LargeStep: number = 5;

    static defaultProps = {
        maxQuantity: 99,
        increaseLabel: 'Increase',
        decreaseLabel: 'Decrease',
        componentFocusedClassName: 'focus-outline',
        arialabel: 'Quantity Select'
    }

    constructor(props) {
        super(props);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.state = {            
            isFocused: false
        };
    }

    handleFocus = () => {
        this.setState({ isFocused: true });
    };

    handleBlur = () => {
        this.setState({ isFocused: false });
    };

    handleKeyDown = (event: React.KeyboardEvent) => {
        const { canAdd, canRemove, maxQuantity } = this.props;

        switch (event.key) {
            case 'ArrowUp':
            case 'ArrowDown':
            case 'PageUp':
            case 'PageDown':
            case 'Home':
            case 'End':
                event.preventDefault();
                break;
            default:
                break;
        }

        switch (event.key) {
            case 'ArrowUp':
                if (canAdd) {
                    this._onPlusClick();
                }
                break;
            case 'ArrowDown':
                if (canRemove) {
                    this._onMinusClick();
                }
                break;
            case 'PageUp':
                if (canAdd) {
                    this._largeStepUp();
                }
                break;
            case 'PageDown':
                if (canRemove) {
                    this._largeStepDown();
                }
                break;
            case 'Home':
                if (canRemove) {
                    this.props.onSelectQuantity(0);
                }
                break;
            case 'End':
                if (canAdd && maxQuantity) {
                    this.props.onSelectQuantity(maxQuantity);
                }
                break;
            default:
                break;
        }
    };

    render() {
        const { isFocused } = this.state;
        const { componentClassName, componentFocusedClassName, canAdd, canRemove, decreaseLabel, quantity, increaseLabel, arialabel, maxQuantity } = this.props;

        return (
            <div className={`${componentClassName} ${isFocused ? componentFocusedClassName : ''}`}
                role="group"
                tabIndex={0}
                aria-label={arialabel}
                onKeyDown={this.handleKeyDown}
                onFocus={this.handleFocus}
                onBlur={this.handleBlur}>

                {/* Decrease Button */}
                <button
                    className="ticket-selection-quantity minus"
                    onClick={this._onMinusClick}
                    disabled={!canRemove}
                    tabIndex={-1}
                    aria-label={decreaseLabel}
                >                    
                    <svg role="presentation" dangerouslySetInnerHTML={{ __html: '<use xlink:href="#icon-minus" />' }} />
                </button>

                <div
                    className="seat-info-prices-qty"
                    tabIndex={-1}
                    aria-valuenow={quantity}
                    role='spinbutton'
                    aria-valuemin={0}
                    aria-valuemax={maxQuantity}                    
                >
                    {quantity}
                </div>

                {/* Increase Button */}
                <button
                    className="ticket-selection-quantity"
                    onClick={this._onPlusClick}
                    disabled={!canAdd}
                    tabIndex={-1}
                    aria-label={increaseLabel}
                >                    
                    <svg role="presentation" dangerouslySetInnerHTML={{ __html: '<use xlink:href="#icon-plus" />' }} />
                </button>
            </div>
        );
    }

    _onPlusClick = () => {
        const { quantity, canAdd, onSelectQuantity } = this.props;

        if (canAdd) {
            const newQuantity = quantity + QuantitySelect.SmallStep;
            onSelectQuantity(newQuantity);
        }
    };

    _onMinusClick = () => {
        const { quantity, canRemove, onSelectQuantity } = this.props;

        if (canRemove) {
            const newQuantity = quantity - QuantitySelect.SmallStep;
            onSelectQuantity(newQuantity);
        }
    };

    _largeStepUp = () => {
        const { quantity, canAdd, maxQuantity, onSelectQuantity } = this.props;
        if (canAdd && maxQuantity) {
            const newQuantity = Math.min(quantity + QuantitySelect.LargeStep, maxQuantity);
            onSelectQuantity(newQuantity);
        }
    };

    _largeStepDown = () => {
        const { quantity, canRemove, maxQuantity, onSelectQuantity } = this.props;

        if (canRemove && maxQuantity) {
            const newQuantity = Math.max(quantity - QuantitySelect.LargeStep, 0);
            onSelectQuantity(newQuantity);
        }

    };
}

export default QuantitySelect;